import { useState, useEffect, useRef } from 'react';
import {
  Mail, Clock, Send, Plus, ArrowLeft, ChevronDown, ChevronUp,
  MessageSquare, CheckCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

interface AdminMessage {
  id: string;
  subject: string;
  message: string;
  read: boolean;
  created_at: string;
  _source: 'admin';
}

interface MessageThread {
  id: string;
  subject: string;
  status: string;
  last_message_at: string;
  created_at: string;
  unread_count: number;
  last_message_preview: string;
  last_sender_type: string;
  _source: 'thread';
}

type InboxItem = AdminMessage | MessageThread;

interface ThreadMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  sender_type: string;
  message_body: string;
  is_read: boolean;
  created_at: string;
}

type PanelMode = 'list' | 'admin-detail' | 'thread-detail' | 'compose';

interface AdminMessagesProps {
  hideOuterCard?: boolean;
}

export default function AdminMessages({ hideOuterCard }: AdminMessagesProps = {}) {
  const { user } = useAuth();
  const [adminMessages, setAdminMessages] = useState<AdminMessage[]>([]);
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<PanelMode>('list');
  const [expandedAdminMsg, setExpandedAdminMsg] = useState<AdminMessage | null>(null);
  const [selectedThread, setSelectedThread] = useState<MessageThread | null>(null);
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    fetchAll();

    const adminChannel = supabase
      .channel('unified-admin-messages')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_messages', filter: `to_client_id=eq.${user.id}` }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_threads' }, fetchAll)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'thread_messages' }, (payload) => {
        if (selectedThread && payload.new && (payload.new as any).thread_id === selectedThread.id) {
          fetchThreadMessages(selectedThread.id);
        }
        fetchAll();
      })
      .subscribe();

    return () => { supabase.removeChannel(adminChannel); };
  }, [user]);

  useEffect(() => {
    if (messagesEndRef.current && mode === 'thread-detail') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadMessages, mode]);

  const fetchAll = async () => {
    await Promise.all([fetchAdminMessages(), fetchThreads()]);
    setLoading(false);
  };

  const fetchAdminMessages = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('admin_messages')
      .select('*')
      .eq('to_client_id', user.id)
      .order('created_at', { ascending: false });
    if (!error && data) {
      setAdminMessages(data.map(m => ({ ...m, _source: 'admin' as const })));
    }
  };

  const fetchThreads = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('message_threads')
      .select('*')
      .eq('workday_customer_id', user.id)
      .order('last_message_at', { ascending: false });
    if (error || !data) return;

    const enriched = await Promise.all(data.map(async (thread) => {
      const { data: lastMsg } = await supabase
        .from('thread_messages')
        .select('message_body, sender_type')
        .eq('thread_id', thread.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const { count: unread } = await supabase
        .from('thread_messages')
        .select('*', { count: 'exact', head: true })
        .eq('thread_id', thread.id)
        .eq('sender_type', 'admin')
        .eq('is_read', false);

      return {
        ...thread,
        _source: 'thread' as const,
        last_message_preview: lastMsg?.message_body || '',
        last_sender_type: lastMsg?.sender_type || 'admin',
        unread_count: unread || 0,
      };
    }));

    setThreads(enriched);
  };

  const fetchThreadMessages = async (threadId: string) => {
    setLoadingMessages(true);
    const { data, error } = await supabase
      .from('thread_messages')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setThreadMessages(data);
      const unread = data.filter(m => m.sender_type === 'admin' && !m.is_read);
      for (const msg of unread) {
        await supabase.from('thread_messages').update({ is_read: true }).eq('id', msg.id);
      }
      fetchThreads();
    }
    setLoadingMessages(false);
  };

  const markAdminMsgRead = async (id: string) => {
    await supabase.from('admin_messages').update({ read: true, read_at: new Date().toISOString() }).eq('id', id);
    fetchAdminMessages();
  };

  const openAdminMsg = (msg: AdminMessage) => {
    if (!msg.read) markAdminMsgRead(msg.id);
    setExpandedAdminMsg(msg);
    setMode('admin-detail');
  };

  const openThread = (thread: MessageThread) => {
    setSelectedThread(thread);
    fetchThreadMessages(thread.id);
    setMode('thread-detail');
  };

  const toggleRowExpand = (rowKey: string) => {
    setExpandedRows(prev => {
      const n = new Set(prev);
      if (n.has(rowKey)) n.delete(rowKey);
      else n.add(rowKey);
      return n;
    });
  };

  const handleReply = async () => {
    if (!selectedThread || !replyMessage.trim()) return;
    setSending(true);
    const { data: { user: u } } = await supabase.auth.getUser();
    if (!u) { setSending(false); return; }
    const { error } = await supabase.from('thread_messages').insert({
      thread_id: selectedThread.id,
      sender_id: u.id,
      sender_type: 'client',
      message_body: replyMessage,
    });
    if (!error) {
      setReplyMessage('');
      fetchThreadMessages(selectedThread.id);
    }
    setSending(false);
  };

  const handleComposeNew = async () => {
    if (!newSubject.trim() || !newMessage.trim()) return;
    setSending(true);
    const { data: { user: u } } = await supabase.auth.getUser();
    if (!u) { setSending(false); return; }

    const { data: thread, error: threadError } = await supabase
      .from('message_threads')
      .insert({ workday_customer_id: u.id, subject: newSubject, created_by: u.id })
      .select()
      .single();
    if (threadError) { setSending(false); return; }

    const { error: msgErr } = await supabase.from('thread_messages').insert({
      thread_id: thread.id,
      sender_id: u.id,
      sender_type: 'client',
      message_body: newMessage,
    });

    if (!msgErr) {
      setNewSubject('');
      setNewMessage('');
      setSelectedThread({ ...thread, _source: 'thread', last_message_preview: newMessage, last_sender_type: 'client', unread_count: 0 });
      fetchThreadMessages(thread.id);
      setMode('thread-detail');
      fetchThreads();
    }
    setSending(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateFull = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const allItems: InboxItem[] = [
    ...adminMessages,
    ...threads,
  ].sort((a, b) => {
    const aDate = '_source' in a && a._source === 'admin' ? a.created_at : (a as MessageThread).last_message_at || a.created_at;
    const bDate = '_source' in b && b._source === 'admin' ? b.created_at : (b as MessageThread).last_message_at || b.created_at;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  const totalUnread =
    adminMessages.filter(m => !m.read).length +
    threads.reduce((sum, t) => sum + t.unread_count, 0);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={hideOuterCard ? '' : 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(mode === 'admin-detail' || mode === 'thread-detail' || mode === 'compose') && (
              <button
                onClick={() => { setMode('list'); setExpandedAdminMsg(null); setSelectedThread(null); setReplyMessage(''); setNewSubject(''); setNewMessage(''); }}
                className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">
              {mode === 'compose' ? 'New Message' :
               mode === 'admin-detail' && expandedAdminMsg ? expandedAdminMsg.subject :
               mode === 'thread-detail' && selectedThread ? selectedThread.subject :
               hideOuterCard ? 'Inbox' : 'Messages from Perplexity Team'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {totalUnread > 0 && mode === 'list' && (
              <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                {totalUnread} new
              </span>
            )}
            {mode === 'list' && (
              <button
                onClick={() => setMode('compose')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                New
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Compose view */}
      {mode === 'compose' && (
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">To</label>
            <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600">Perplexity Team</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Subject</label>
            <input
              type="text"
              value={newSubject}
              onChange={e => setNewSubject(e.target.value)}
              placeholder="Message subject..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Message</label>
            <textarea
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              rows={6}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleComposeNew}
              disabled={sending || !newSubject.trim() || !newMessage.trim()}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send'}
            </button>
            <button
              onClick={() => setMode('list')}
              className="px-5 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Admin message detail */}
      {mode === 'admin-detail' && expandedAdminMsg && (
        <div className="flex flex-col">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">From Perplexity Team</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
              <Clock className="w-3 h-3" />
              {formatDateFull(expandedAdminMsg.created_at)}
            </div>
          </div>
          <div className="p-5 max-h-72 overflow-y-auto">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{expandedAdminMsg.message}</p>
          </div>
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
            <button
              onClick={() => { setMode('list'); setExpandedAdminMsg(null); }}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Thread detail */}
      {mode === 'thread-detail' && selectedThread && (
        <div className="flex flex-col">
          <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedThread.status === 'open'
                ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Active</span>
                : <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />Closed</span>}
            </div>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto space-y-3">
            {loadingMessages ? (
              <div className="text-center py-6 text-gray-400 text-sm">Loading messages...</div>
            ) : threadMessages.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">No messages yet</div>
            ) : (
              threadMessages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
                    msg.sender_type === 'client'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold opacity-80">
                        {msg.sender_type === 'client' ? 'You' : 'Perplexity Team'}
                      </span>
                      <span className="text-xs opacity-60">{formatDate(msg.created_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message_body}</p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <textarea
                value={replyMessage}
                onChange={e => setReplyMessage(e.target.value)}
                placeholder="Reply to Perplexity Team..."
                rows={2}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <button
                onClick={handleReply}
                disabled={sending || !replyMessage.trim()}
                className="self-end flex items-center justify-center w-9 h-9 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inbox list */}
      {mode === 'list' && (
        <>
          {allItems.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 text-sm mb-3">No messages yet</p>
              <button onClick={() => setMode('compose')} className="text-blue-600 text-sm hover:text-blue-800 transition-colors">
                Send your first message to the Perplexity Team
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {allItems.map(item => {
                const isAdmin = item._source === 'admin';
                const key = isAdmin ? `admin-${item.id}` : `thread-${item.id}`;
                const isExpanded = expandedRows.has(key);
                const isUnread = isAdmin
                  ? !(item as AdminMessage).read
                  : (item as MessageThread).unread_count > 0;
                const itemDate = isAdmin ? item.created_at : (item as MessageThread).last_message_at || item.created_at;
                const preview = isAdmin
                  ? (item as AdminMessage).message
                  : ((item as MessageThread).last_sender_type === 'client' ? 'You: ' : 'Perplexity: ') + (item as MessageThread).last_message_preview;

                return (
                  <div key={key} className={isUnread ? 'bg-blue-50' : 'bg-white'}>
                    {/* Row header - always visible, clickable to expand inline or open detail */}
                    <div
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        if (isAdmin) {
                          if (!isExpanded) {
                            const msg = item as AdminMessage;
                            if (!msg.read) markAdminMsgRead(msg.id);
                          }
                          toggleRowExpand(key);
                        } else {
                          openThread(item as MessageThread);
                        }
                      }}
                    >
                      {/* Unread dot */}
                      <div className="w-2 flex-shrink-0 flex justify-center">
                        {isUnread && <span className="w-2 h-2 bg-blue-600 rounded-full block"></span>}
                      </div>

                      {/* Icon */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isAdmin ? 'bg-blue-100' : 'bg-teal-100'
                      }`}>
                        {isAdmin
                          ? <Mail className="w-4 h-4 text-blue-600" />
                          : <MessageSquare className="w-4 h-4 text-teal-600" />}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm truncate ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                            {item.subject}
                          </span>
                          {!isAdmin && (item as MessageThread).unread_count > 0 && (
                            <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                              {(item as MessageThread).unread_count}
                            </span>
                          )}
                        </div>
                        {!isExpanded && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">{preview}</p>
                        )}
                      </div>

                      {/* Date + expand indicator */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400">{formatDate(itemDate)}</span>
                        {isAdmin ? (
                          isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : null}
                      </div>
                    </div>

                    {/* Collapsible body for admin messages */}
                    {isAdmin && isExpanded && (
                      <div className="px-4 pb-3 pt-1 border-t border-blue-100 bg-blue-50/50">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed mb-2">
                          {(item as AdminMessage).message}
                        </p>
                        <span className="text-xs text-gray-400">{formatDateFull(item.created_at)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
